import { Fragment, useState } from "react";
import { useDraftRows, UnsavedBar } from "@/components/useDraftRows";
import { Plus, Trash2, Save, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Cell } from "@/components/EditableTable";

export const isImp = (r) => r.billing_type === "impression" || r.billing_type === "per_1000";
export const isArea = (r) => r.billing_type === "sqft" || r.billing_type === "sqm";
const MAIN = [["code", "Code", "12%"], ["name", "Machine", "16%"], ["service_line", "Line", "12%"], ["billing_type", "Billing type", "14%"]];
const TAIL = [["speed_per_hour", "Speed / h", "11%"], ["setup_minutes", "Setup min", "8%"]];
const SIZE = ["max_sheet_size", "max_w", "max_h"];
const MORE = ["power_kw", "daily_capacity_hours"];

function Fld({ col, r, i, set, testId, label }) {
  return (
    <div className="min-w-0 [&_input]:min-w-0 [&_input]:px-2 [&_select]:pl-1.5 [&_select]:pr-6">
      {label && <span className="block text-[11px] text-slate-500">{label === true ? col.label : label}</span>}
      <Cell col={col} row={r} value={r[col.key]} onChange={(v) => set(i, col.key, v)} testId={`${testId}-${i}-${col.key}`} />
      {col.suffix && !col.disabledIf?.(r) && <span className="block break-words text-[10px] leading-tight text-slate-400" data-testid={`${testId}-${i}-${col.key}-unit`}>{col.suffix(r)}</span>}
    </div>
  );
}

function Rates({ r, i, set, testId, by }) {
  const keys = isImp(r) ? ["simplex_rate", "duplex_rate"] : isArea(r) ? ["area_rate"] : ["hourly_rate"];
  return <div className={`grid gap-2 ${keys.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid={`${testId}-${i}-rates`}>{keys.map((k) => <Fld key={k} col={by[k]} r={r} i={i} set={set} testId={testId} label={keys.length > 1 && (k === "simplex_rate" ? "Simplex (1 side)" : "Duplex (2 sides)")} />)}</div>;
}

function Extra({ r, i, set, testId, by }) {
  return (
    <div className="grid gap-4 bg-slate-50 px-4 py-3 sm:grid-cols-2" data-testid={`${testId}-${i}-size-panel`}>
      <div><p className="mb-1 text-xs font-semibold text-slate-700">Size limits <span className="font-normal text-slate-500">· only used to count impressions</span></p>
        <div className="grid grid-cols-3 gap-2">{SIZE.map((k) => <Fld key={k} col={by[k]} r={r} i={i} set={set} testId={testId} label />)}</div></div>
      <div><p className="mb-1 text-xs font-semibold text-slate-700">Capacity &amp; power</p>
        <div className="grid grid-cols-2 gap-2">{MORE.map((k) => <Fld key={k} col={by[k]} r={r} i={i} set={set} testId={testId} label />)}</div></div>
    </div>
  );
}

const withBilling = (r) => ({ ...r, billing_type: r.billing_type || (r.service_line === "large_format" ? "sqft" : "hourly") });

/** Machine rates: compact rows (name + billing type always visible); impression rates replace hourly rate; size limits collapsed per row. */
export function MachineRates({ columns, rows, onSave, testId, readOnly, onDiscard }) {
  const by = Object.fromEntries(columns.map((c) => [c.key, c]));
  const { data, setData, dirty, discard } = useDraftRows(rows, testId, withBilling, onDiscard);
  const [open, setOpen] = useState({});
  const isOpen = (r, i) => open[i] ?? !!r._draft;
  const set = (i, k, v) => setData((d) => d.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const n = MAIN.length + TAIL.length + 2;
  return (
    <div data-testid={testId}>
      <UnsavedBar dirty={dirty} onDiscard={discard} testId={testId} />
      <div className="overflow-x-auto"><table className="po-table w-full min-w-[760px] table-fixed lg:min-w-0" data-testid={`${testId}-table`}>
        <colgroup>{MAIN.map(([k, , w]) => <col key={k} style={{ width: w }} />)}<col style={{ width: "19%" }} />{TAIL.map(([k, , w]) => <col key={k} style={{ width: w }} />)}<col style={{ width: "8%" }} /></colgroup>
        <thead><tr>{MAIN.map(([k, l]) => <th key={k}>{l}</th>)}<th>Rate</th>{TAIL.map(([k, l]) => <th key={k}>{l}</th>)}<th /></tr></thead>
        <tbody>
          {data.map((r, i) => (
            <Fragment key={i}>
              <tr data-testid={`${testId}-row-${i}`}>
                {MAIN.map(([k]) => <td key={k} className="!px-1.5 !py-1.5 align-top"><Fld col={by[k]} r={r} i={i} set={set} testId={testId} />{k === "service_line" && r.service_line && <span className="block break-words text-[10px] leading-tight text-slate-500" data-testid={`${testId}-${i}-service_line-full`}>{by[k].labelOf ? by[k].labelOf(r.service_line) : r.service_line}</span>}
                  {k === "service_line" && !r.service_line && <span className="block text-[10px] font-medium leading-tight text-rose-600" data-testid={`${testId}-${i}-no-line`}>No line chosen: this machine won&apos;t show in quotes</span>}
                  {k === "code" && !r.code && <span className="block text-[10px] leading-tight text-amber-700" data-testid={`${testId}-${i}-no-code`}>{r.name ? "A code is set when you save" : "Enter a name"}</span>}</td>)}
                <td className="!px-1.5 !py-1.5 align-top"><Rates r={r} i={i} set={set} testId={testId} by={by} /></td>
                {TAIL.map(([k]) => <td key={k} className="!px-1.5 !py-1.5 align-top"><Fld col={by[k]} r={r} i={i} set={set} testId={testId} /></td>)}
                <td className="!px-2 !py-1.5 align-top"><div className="flex items-center gap-1.5">
                  <button type="button" title="Size limits" aria-expanded={isOpen(r, i)} data-testid={`${testId}-${i}-size-toggle`} className="text-slate-400 hover:text-[#389F95]" onClick={() => setOpen({ ...open, [i]: !isOpen(r, i) })}>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen(r, i) ? "rotate-180" : ""}`} /></button>
                  {!readOnly && <button type="button" data-testid={`${testId}-remove-${i}`} className="text-slate-400 hover:text-rose-600" onClick={() => setData(data.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>}
                </div></td>
              </tr>
              {isOpen(r, i) && <tr><td colSpan={n} className="!p-0"><Extra r={r} i={i} set={set} testId={testId} by={by} /></td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table></div>
      {!readOnly && (
        <div className="flex gap-2 border-t border-slate-100 p-3">
          <Button size="sm" variant="outline" data-testid={`${testId}-add`} onClick={() => setData([...data, { billing_type: "hourly" }])}><Plus className="mr-1 h-4 w-4" />Add row</Button>
          <Button size="sm" data-testid={`${testId}-save`} onClick={() => onSave(data.map(({ _draft, ...r }) => r))} className="bg-[#13294B] hover:bg-[#1c3a66]"><Save className="mr-1 h-4 w-4" />Save changes</Button>
        </div>
      )}
    </div>
  );
}
